import {
  ApiError,
  CatalogueAvailabilityController_list_v1,
  CatalogueAvailabilityController_replace_v1,
  type CatalogueAvailabilityController_replace_v1Body,
  type CatalogueItemResource,
  CatalogueItemsController_read_v1,
  type OutletResource,
  OutletsController_list_v1,
} from '@repo/api-client';
import { Alert, AlertDescription, AlertTitle } from '@repo/ui/components/alert';
import { Badge } from '@repo/ui/components/badge';
import { Button } from '@repo/ui/components/button';
import { Checkbox } from '@repo/ui/components/checkbox';
import { TableBody, TableCell, TableHead, TableHeader, TableRow } from '@repo/ui/components/table';
import { IconAlertTriangle, IconArrowLeft, IconCircleCheck, IconLock } from '@tabler/icons-react';
import { useCallback, useEffect, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router';

import { useApiClient } from '@/api/api-client';
import { collectPages } from '@/api/collect-pages';
import { isForbidden, isNotFound, messageForFailure } from '@/api/failure';
import { IDEMPOTENCY_KEY_HEADER, newIdempotencyKey } from '@/lib/idempotency-key';
import { formatPaise } from '@/lib/money';

import {
  EmptyState,
  LoadFailed,
  Screen,
  ScreenFact,
  ScreenTable,
  TableNotice,
  TableSkeleton,
} from '../back-office/Screen';
import { labelForKind as labelForOutletKind, labelForStock } from '../outlets/outlet-kinds';
import { paths } from '../paths';
import { barcodeRefusal, barcodeSubject } from './barcode-refusal';
import { isRetired, labelForKind } from './catalogue-kinds';

/**
 * Which outlets may sell one item: `GET` and `PUT
 * /v1/catalogue/items/{id}/outlets`.
 *
 * ## The `PUT` replaces the whole set, and this screen is shaped around that
 *
 * The body is **the complete set of outlets after the call**, not an addition to
 * it. An outlet that sells the item today and is absent from the body is
 * withdrawn. This is the most misreadable route in the epic, and the shape that
 * gets it wrong is the obvious one: a dialog with a "add an outlet" control that
 * sends what was just ticked.
 *
 * So the control is a **register of every outlet the park has**, each with a box
 * saying whether this item sells there, and one Save that sends every ticked box.
 * There is no add and no remove — unticking *is* withdrawing, and the copy says
 * so above the button rather than in a tooltip. The count in the rail reads "3 of
 * 9 outlets" for the same reason: a set has a size and a total, an add button has
 * neither.
 *
 * **Prefill from the `GET` and send it back to the `PUT`.** The API's own
 * document asks for exactly that and says why: the two are answered by the same
 * query, so what one returns is what the other accepts. Identity shipped a defect
 * where a screen prefilled from a role-derived field and wrote rows that had
 * never existed (`Staffing.tsx` records it); there is nothing role-derived here,
 * and the habit is the point.
 *
 * ## Read whole, and that is the choice rather than the default
 *
 * `docs/ui-conventions.md`, "Reading a list", asks for the strategy to be picked.
 * An item's availability is bounded by the *park* — a dozen outlets, and fifty
 * would be remarkable — and this screen needs every one of them at once to draw a
 * box per outlet. So both reads are whole, through `collectPages`.
 *
 * The screen it links to, `OutletCatalogue`, is the same rule applied to a set
 * that grows with *use* and comes out the other way: it pages. Getting these the
 * same way round is the mistake the conventions document exists to prevent.
 *
 * ## Outlets that carry no stock are not offered, rather than refused
 *
 * An item that counts stock can only be sold where stock is carried (FR-208), and
 * the API refuses the whole `PUT` with `CATALOGUE_STOCK_NOT_AVAILABLE_HERE` if
 * any named outlet carries none. The gate and the four activity centres carry no
 * stock, so for a stock-tracked product that is five boxes out of nine that can
 * only ever produce a refusal — `docs/ui-conventions.md` asks that a control the
 * caller cannot use be unavailable rather than shown and refused, so they are
 * left out of the table and named underneath it instead.
 *
 * **With one exception, and it is the one that matters.** An outlet the item is
 * *already* sold at is always listed, even if it carries no stock. Filtering one
 * of those out would hide a ticked box from a form that submits the whole set —
 * which is this screen silently withdrawing availability nobody asked it to
 * withdraw, the exact failure the file is written against. The API's four doors
 * make that pair unreachable today; the row is listed anyway, because a rule
 * enforced elsewhere is not a reason for this file to be unsafe.
 *
 * ## After a save, the set is what the server says it is
 *
 * The `PUT` answers with the availability as it now stands, and the table is
 * re-seeded from that answer rather than from the request. They agree today; a
 * screen that derives its state from what it *asked for* is a screen that would
 * stop agreeing without anybody noticing. The server takes a row lock and
 * serialises concurrent saves, so the answer is never a merge of two requests —
 * the failure to design for is somebody else having changed this while it was
 * open, and re-seeding from the answer is what shows that.
 */

/** Everything the picker needs, read together. */
interface Availability {
  readonly item: CatalogueItemResource;

  /** Every live outlet in the park — the universe the boxes are drawn from. */
  readonly outlets: readonly OutletResource[];

  /** The outlets this item sells at now. Seeds the ticks, and nothing else does. */
  readonly available: readonly OutletResource[];
}

/** One read, in one of the three states it can be in. */
type LoadState =
  | { readonly kind: 'loading' }
  | { readonly kind: 'loaded'; readonly value: Availability }
  | { readonly kind: 'failed'; readonly error: unknown };

/** Kept next to the header cells below, so the two cannot drift. */
const COLUMNS = 4;

export function ItemAvailability() {
  const client = useApiClient();
  const { id: itemId } = useParams<{ id: string }>();

  const [state, setState] = useState<LoadState>({ kind: 'loading' });

  /**
   * The outlets ticked right now — exactly what Save will send.
   *
   * Seeded from the item's current availability and from nothing else, and
   * re-seeded from the response after every successful save.
   */
  const [ticked, setTicked] = useState<ReadonlySet<string>>(new Set());

  const [saving, setSaving] = useState(false);

  // What the save was refused with, kept apart from the load failure: one is
  // about the screen not being readable and the other about a write that did not
  // happen, and they are true at different moments.
  const [saveError, setSaveError] = useState<string | null>(null);

  const [notice, setNotice] = useState<string | null>(null);

  // A counter rather than a boolean, so two reloads in a row are two distinct
  // values and the second one still fires.
  const [reloads, setReloads] = useState(0);
  const reload = useCallback(() => setReloads((count) => count + 1), []);

  useEffect(() => {
    if (itemId === undefined) return;

    // Two guards, not one. `controller` stops the requests themselves, and
    // `live` stops an answer already in flight from being written into state
    // after this effect was torn down — without it a reload can land the first
    // answer on top of the second, and this screen *writes what it is showing*.
    const controller = new AbortController();
    let live = true;

    setState({ kind: 'loading' });
    setSaveError(null);

    // The notice is deliberately **not** cleared here. One of the two ways this
    // effect re-runs is a save that answered with a truncated page and asked for
    // a re-read — clearing it there would wipe the confirmation of the save that
    // caused the read, a frame after it was set.

    // All three together. A table of boxes is not drawable from any two of them,
    // and `Promise.all` rejecting on the first failure is right for the same
    // reason: a picker missing its prefill would render every box clear, which
    // reads as "sold nowhere" and is one Save away from being true.
    Promise.all([
      client.request(CatalogueItemsController_read_v1, {
        path: { id: itemId },
        signal: controller.signal,
      }),
      collectPages<OutletResource>((offset, limit) =>
        client.request(OutletsController_list_v1, {
          // Live outlets only, which is the API's default. A retired outlet
          // cannot be named in the `PUT` — it is refused as `RESOURCE_NOT_FOUND`
          // — so offering a box for one would be offering a refusal.
          query: { offset, limit },
          signal: controller.signal,
        }),
      ),
      collectPages<OutletResource>((offset, limit) =>
        client.request(CatalogueAvailabilityController_list_v1, {
          path: { id: itemId },
          query: { offset, limit },
          signal: controller.signal,
        }),
      ),
    ])
      .then(([item, outlets, available]) => {
        if (!live) return;

        setState({ kind: 'loaded', value: { item, outlets, available } });
        setTicked(new Set(available.map((outlet) => outlet.id)));
      })
      .catch((error: unknown) => {
        if (live) setState({ kind: 'failed', error });
      });

    return () => {
      live = false;
      controller.abort();
    };
  }, [client, itemId, reloads]);

  const loaded = state.kind === 'loaded' ? state.value : null;
  const item = loaded?.item ?? null;
  const retired = item !== null && isRetired(item);

  const rows = loaded === null ? [] : offerableOutlets(loaded);
  const withheld = loaded === null ? [] : withheldOutlets(loaded);

  const saved =
    loaded === null ? new Set<string>() : new Set(loaded.available.map((outlet) => outlet.id));

  const changed = loaded !== null && !sameSet(ticked, saved);

  /**
   * One box moved.
   *
   * It clears the last confirmation as well as toggling. That message names the
   * set as it stands on the server, and leaving it under a table somebody has
   * started changing makes it a description of the boxes in front of them —
   * which it no longer is.
   */
  const toggle = (outletId: string, next: boolean) => {
    setNotice(null);
    setSaveError(null);

    setTicked((current) => {
      const updated = new Set(current);

      if (next) updated.add(outletId);
      else updated.delete(outletId);

      return updated;
    });
  };

  const handleSave = async () => {
    if (itemId === undefined || loaded === null || saving) return;

    setSaveError(null);
    setNotice(null);
    setSaving(true);

    try {
      // The shape comes from the generated client. `ticked` is seeded from the
      // matching `GET` and only ever changed by somebody clicking a box, so this
      // is the availability set — sorted, so two saves of the same set read as
      // the same request in a log.
      const body: CatalogueAvailabilityController_replace_v1Body = {
        outletIds: [...ticked].sort(),
      };

      // Minted per save and not per mount, the same as `Staffing`. This is a
      // screen rather than a dialog: it stays open after a successful save and
      // the next save is a different set, so a key held for the component's life
      // would present the same `(scope, key)` with a changed payload and earn
      // `SYNC_IDEMPOTENCY_KEY_REUSED` — a non-retryable 409 on a screen whose
      // whole job is to be saved more than once.
      //
      // The cost is that a retry after a dropped answer writes again rather than
      // replaying. Harmless here for the same reason it is harmless there: this
      // route replaces the set rather than adding to it, so writing it twice
      // lands the same rows.
      const answer = await client.request(CatalogueAvailabilityController_replace_v1, {
        path: { id: itemId },
        body,
        headers: { [IDEMPOTENCY_KEY_HEADER]: newIdempotencyKey() },
      });

      // Re-seeded from the answer, which is the availability as it now stands —
      // not from `body`, which is only what was asked for.
      //
      // Unless the answer was truncated. The `PUT` accepts at most one page's
      // worth of outlets, so this can only happen in a park with more outlets
      // than that — but a partial page read as a whole set is exactly the defect
      // this screen exists to avoid, in a second costume, so it re-reads instead.
      if (answer.items.length < answer.total) {
        reload();

        // Not `savedMessage`: naming a set from a page that is admittedly
        // incomplete is the same mistake one sentence further on. The re-read
        // will say what it is.
        setNotice(
          `Where ${loaded.item.name} sells has been saved. The list is being read back to show what it is now.`,
        );
      } else {
        setState({ kind: 'loaded', value: { ...loaded, available: answer.items } });
        setTicked(new Set(answer.items.map((outlet) => outlet.id)));
        setNotice(savedMessage(loaded.item, answer.items));
      }
    } catch (error) {
      // A barcode refusal on a form that never mentions barcodes, so the
      // number goes in the sentence along with the item that already has it.
      setSaveError(
        error instanceof ApiError && error.code === 'CATALOGUE_STOCK_NOT_AVAILABLE_HERE'
          ? stockRefusal(loaded.outlets, ticked)
          : error instanceof ApiError && error.code === 'CATALOGUE_BARCODE_TAKEN'
            ? barcodeRefusal(error, loaded.outlets, barcodeSubject(loaded.item))
            : messageForFailure(error),
      );

      // Nothing is cleared and nothing is re-read. The ticks are what the person
      // meant; a refusal that wipes them makes the correction a re-entry.
    } finally {
      setSaving(false);
    }
  };

  // A route matched on `:id` always has one; this is the shape of somebody
  // reaching the URL some other way. A redirect rather than a not-found screen,
  // because the catalogue is where they were going.
  if (itemId === undefined) return <Navigate to={paths.catalogue} replace />;

  const refused = state.kind === 'failed' && isForbidden(state.error);
  const missing = state.kind === 'failed' && isNotFound(state.error);

  return (
    <Screen
      title="Where it sells"
      description="Which outlets may sell this item. This is the whole set: saving replaces it, so an outlet left unticked stops selling the item. Nothing about it is permanent — the same outlet can be added back later."
      aside={
        <>
          {item !== null && (
            <ScreenFact label="Item">
              <span className="font-mono numeric">{item.code}</span> — {item.name}
              {retired && (
                <>
                  {' '}
                  {/* `outline` and not `destructive`: retiring an item is an
                      ordinary end to an ordinary life, and red is reserved for a
                      system that refused something or is about to. */}
                  <Badge variant="outline">Retired</Badge>
                </>
              )}
              <br />
              <span className="text-muted-foreground">
                {labelForKind(item.kind)}, {formatPaise(item.sellingPaise)}
                {item.tracksStock && ', counts stock'}
              </span>
            </ScreenFact>
          )}

          {loaded !== null && !retired && (
            <ScreenFact label="Sells at">
              <span className="font-mono numeric">{ticked.size}</span> of{' '}
              <span className="font-mono numeric">{rows.length}</span>{' '}
              {rows.length === 1 ? 'outlet' : 'outlets'}
              {changed && ' — not saved yet'}
            </ScreenFact>
          )}

          <Link
            to={paths.catalogue}
            className="inline-flex items-center gap-1.5 rounded-3xl text-sm text-muted-foreground underline-offset-4 outline-none hover:text-foreground hover:underline focus-visible:ring-3 focus-visible:ring-ring/30"
          >
            <IconArrowLeft className="size-4" aria-hidden />
            Back to the catalogue
          </Link>
        </>
      }
    >
      {refused ? (
        <OwnerOnly />
      ) : missing ? (
        <ItemMissing />
      ) : (
        <>
          {notice !== null && (
            // `status` and not the component's default `alert`: a save that
            // worked is not an interruption, and it is announced politely rather
            // than cutting across whatever is being read.
            <Alert role="status">
              <IconCircleCheck />
              <AlertDescription>{notice}</AlertDescription>
            </Alert>
          )}

          {retired && item !== null && (
            <Alert>
              <IconAlertTriangle />
              <AlertTitle>This item is retired</AlertTitle>
              <AlertDescription>
                Where it sold is kept and shown below, and none of it can be changed while it is
                retired — the API refuses the write. Restore it from the catalogue first if it
                should be on sale again.
              </AlertDescription>
            </Alert>
          )}

          <ScreenTable label="Outlets">
            <TableHeader>
              <TableRow>
                <TableHead className="w-20">Sells it</TableHead>
                <TableHead>Outlet</TableHead>
                <TableHead className="w-44">Kind</TableHead>
                <TableHead className="w-44">Stock</TableHead>
              </TableRow>
            </TableHeader>

            {state.kind === 'loading' && <TableSkeleton columns={COLUMNS} />}

            {state.kind === 'failed' && (
              <TableNotice columns={COLUMNS}>
                <LoadFailed error={state.error} onRetry={reload} />
              </TableNotice>
            )}

            {state.kind === 'loaded' &&
              (rows.length === 0 ? (
                <TableNotice columns={COLUMNS}>
                  <EmptyState
                    title={
                      withheld.length === 0 ? 'No outlets yet' : 'No outlet here can sell this item'
                    }
                    description={
                      withheld.length === 0
                        ? 'Nothing can be sold anywhere until the park has an outlet. Create the first one on the Outlets screen.'
                        : 'This item counts stock, and every outlet in the park is set to hold none. Turn on Holds stock for the outlet that should sell it, or turn off Counts stock on the item.'
                    }
                  />
                </TableNotice>
              ) : (
                <TableBody>
                  {rows.map((outlet) => (
                    <OutletChoiceRow
                      key={outlet.id}
                      outlet={outlet}
                      ticked={ticked.has(outlet.id)}
                      // A retired item accepts no write at all, so the boxes are
                      // readable and inert rather than absent: somebody is here
                      // to look up where it used to sell.
                      disabled={saving || retired}
                      onToggle={(next) => toggle(outlet.id, next)}
                    />
                  ))}
                </TableBody>
              ))}
          </ScreenTable>

          {/* Said under the table it is about, rather than left as five outlets
              somebody notices are missing and assumes is a fault. */}
          {withheld.length > 0 && (
            <p className="max-w-prose text-sm text-muted-foreground">
              {namesOf(withheld)} {withheld.length === 1 ? 'is' : 'are'} not listed:{' '}
              {withheld.length === 1 ? 'it holds' : 'they hold'} no stock, and an item that counts
              stock can only be sold where stock is counted. Turning that on is done on the outlet
              itself — and it cannot be turned back off while a stock-tracked item still sells
              there, which is this same rule from the other side.
            </p>
          )}

          {state.kind === 'loaded' && rows.length > 0 && !retired && (
            <div className="flex flex-wrap items-start justify-between gap-6">
              {/* The consequence somebody hesitating over an unticked box needs,
                  said before they press the button rather than found out after.
                  Both halves matter: withdrawing loses no history, and neither
                  direction reaches a till the instant it is saved. */}
              <p className="max-w-prose text-sm text-muted-foreground">
                Saving sends every ticked box as the whole set. Withdrawing an outlet stops future
                sales there and touches no past one — every sale, ticket and receipt stays exactly
                where it is — and the item can be added back to the same outlet afterwards.
              </p>

              <div className="space-y-3">
                {saveError !== null && (
                  <Alert variant="destructive">
                    <IconAlertTriangle />
                    <AlertDescription>{saveError}</AlertDescription>
                  </Alert>
                )}

                {/* Disabled until the set differs from the one the server last
                    answered with. Replacing a set with itself writes no
                    availability row — the API leaves the ones already there
                    alone — but it is still an audit entry saying somebody opened
                    a screen. There is no wrong-prefill hazard to hide here: the
                    `GET` this is seeded from is the same query the `PUT`
                    accepts, which is not true of the staffing screen next door. */}
                <Button
                  className="w-full sm:w-auto"
                  disabled={saving || !changed}
                  onClick={() => void handleSave()}
                >
                  {saving ? 'Saving…' : 'Save where it sells'}
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </Screen>
  );
}

/**
 * The outlets that get a box.
 *
 * Every live outlet, less the ones that carry no stock when the item counts it —
 * see the note at the top of this file. **An outlet the item already sells at is
 * always kept**, whatever its stock position: a ticked box that is not drawn is a
 * withdrawal nobody asked for the next time Save is pressed.
 */
function offerableOutlets({ item, outlets, available }: Availability): readonly OutletResource[] {
  if (!item.tracksStock) return outlets;

  const sellsHere = new Set(available.map((outlet) => outlet.id));

  return outlets.filter((outlet) => outlet.carriesStock || sellsHere.has(outlet.id));
}

/** The outlets left out by the rule above, for the sentence that names them. */
function withheldOutlets(availability: Availability): readonly OutletResource[] {
  const offered = new Set(offerableOutlets(availability).map((outlet) => outlet.id));

  return availability.outlets.filter((outlet) => !offered.has(outlet.id));
}

/**
 * What a successful save says.
 *
 * It names the set as the server answered it, and then says the one thing
 * somebody walking to the gate to check needs to know: a till learns about this
 * on its next look for new prices, not the moment the button was pressed.
 * Availability moves the outlet's catalogue version in **both** directions, so
 * the sentence covers what was withdrawn as well as what was added — the same
 * wording retirement uses on the catalogue screen.
 */
function savedMessage(item: CatalogueItemResource, outlets: readonly OutletResource[]): string {
  const tills =
    'Every outlet this changed — added or withdrawn — tells its tills on their next check for new prices rather than the moment you pressed save: a poll away, not instant.';

  if (outlets.length === 0) {
    return `${item.name} is no longer sold anywhere. ${tills}`;
  }

  return `${item.name} sells at ${namesOf(outlets)}. ${tills}`;
}

/**
 * What to say when the API refuses the whole set because a ticked outlet carries
 * no stock.
 *
 * **The offending outlet is named from this screen's own copy of the outlets, not
 * from the refusal.** `assertStockIsCarried` selects the offending row and then
 * throws `new AppException('CATALOGUE_STOCK_NOT_AVAILABLE_HERE')` with no second
 * argument, so nothing about which outlet comes back; the same gap is recorded on
 * the create and the edit dialogs and reported again in this PR body.
 *
 * Reaching this at all means the table and the server disagreed, which is real
 * rather than impossible: an outlet's `carriesStock` can be turned off between
 * the read that drew the boxes and the save. The fallback is the rule itself,
 * from `error-messages.ts`, which no screen writes for itself.
 */
function stockRefusal(outlets: readonly OutletResource[], ticked: ReadonlySet<string>): string {
  const without = outlets.filter((outlet) => ticked.has(outlet.id) && !outlet.carriesStock);

  if (without.length === 0) {
    return 'One of the outlets ticked does not carry stock, so it cannot sell an item that counts stock. Nothing was changed. Reload the screen to see where the item stands.';
  }

  const one = without.length === 1;

  return `${namesOf(without)} ${one ? 'holds' : 'hold'} no stock, so ${one ? 'it' : 'they'} cannot sell an item that counts stock. Nothing was changed — the whole set was refused, not just that ${one ? 'outlet' : 'those outlets'}. Untick ${one ? 'it' : 'them'} and save again, or turn on Holds stock for ${one ? 'it' : 'them'} on the outlets screen first.`;
}

/** Outlet names, as a sentence — "Main Gate, Doll Shop and the Cafeteria". */
const namesOf = (outlets: readonly OutletResource[]): string =>
  new Intl.ListFormat('en-IN', { type: 'conjunction' }).format(
    outlets.map((outlet) => outlet.name),
  );

/** Whether the ticked set still matches what the server last said. */
function sameSet(left: ReadonlySet<string>, right: ReadonlySet<string>): boolean {
  if (left.size !== right.size) return false;

  for (const value of left) if (!right.has(value)) return false;

  return true;
}

function OutletChoiceRow({
  outlet,
  ticked,
  disabled,
  onToggle,
}: {
  outlet: OutletResource;
  ticked: boolean;
  disabled: boolean;
  onToggle: (next: boolean) => void;
}) {
  // The outlet's name is the checkbox's accessible name, so a reader moving down
  // the column hears what each box is for rather than "checkbox" nine times.
  // Named from the cell that is already on screen, so the two cannot disagree.
  const nameId = `sells-at-${outlet.id}`;

  return (
    <TableRow>
      <TableCell>
        <Checkbox
          aria-labelledby={nameId}
          checked={ticked}
          disabled={disabled}
          onCheckedChange={(next) => onToggle(next === true)}
        />
      </TableCell>

      <TableCell>
        {/* A `span` and not a `Label`. Base UI's checkbox is a `button` with
            `role="checkbox"` rather than an `input`, so a `<label for>` would
            bind to nothing; `aria-labelledby` above is what names it, and this
            is the text it names. */}
        <span id={nameId} className="text-foreground">
          {outlet.name}
        </span>{' '}
        {/* Read aloud and typed back in, so it keeps the mono face and tabular
            figures it has everywhere else. */}
        <span className="font-mono text-muted-foreground numeric">{outlet.code}</span>
      </TableCell>

      <TableCell>{labelForOutletKind(outlet.kind)}</TableCell>
      <TableCell>
        {labelForStock(outlet.carriesStock)}
        {/* The one row the stock rule lets through despite carrying no stock:
            the item already sells here, so the box is drawn and can be
            unticked, but ticking it back afterwards would be refused. Said on
            the row rather than discovered at the save. */}
        {!outlet.carriesStock && ticked && (
          <span className="block text-xs text-muted-foreground">
            Already selling here. Withdrawing it cannot be undone from this screen.
          </span>
        )}
      </TableCell>
    </TableRow>
  );
}

/**
 * A manager who reached this screen anyway, told where the boundary is.
 *
 * **Not `LoadFailed`.** "This list could not be loaded" is untrue: it was read,
 * and the answer was no. It names the reason rather than the role, in the same
 * spirit as the API's own refusals, and offers no retry — `AUTH_FORBIDDEN` is
 * non-retryable in the API's registry and a Try again under it cannot work.
 */
function OwnerOnly() {
  return (
    <Alert>
      <IconLock />
      <AlertTitle>Only an owner sets where an item sells</AlertTitle>
      <AlertDescription>
        Reading what an outlet sells is open to a manager — the catalogue screen&rsquo;s outlet
        filter, and each outlet&rsquo;s own catalogue, both answer it. Changing the set is the
        owner&rsquo;s alone.
      </AlertDescription>
    </Alert>
  );
}

/** The item behind the URL is not there — a stale link, or one typed by hand. */
function ItemMissing() {
  return (
    <Alert>
      <IconAlertTriangle />
      <AlertTitle>There is no such item</AlertTitle>
      <AlertDescription>
        Nothing in the catalogue has this address. It may have been opened from a link that is out
        of date. Go back to the catalogue and pick the item from the list.
      </AlertDescription>
    </Alert>
  );
}
